import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Zap, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";

export default function BoredNowButton() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(90);

  // Poll for status
  useEffect(() => {
    if (!request || request.status !== "open") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/instant-match/${request.request_id}`);
        setRequest(data);
        if (data.status === "matched" && data.booking_id) {
          toast.success(`${data.provider?.name || "A companion"} matched with you!`);
        }
      } catch {}
    }, 2000);
    return () => clearInterval(t);
  }, [request]);

  // Countdown
  useEffect(() => {
    if (!request || request.status !== "open") return;
    const expires = new Date(request.expires_at).getTime();
    const t = setInterval(() => {
      setSecondsLeft(Math.max(0, Math.round((expires - Date.now()) / 1000)));
    }, 500);
    return () => clearInterval(t);
  }, [request]);

  const start = async () => {
    if (!user) {
      navigate(`/auth?next=${encodeURIComponent("/discovery")}`);
      return;
    }
    try {
      const { data } = await api.post("/instant-match", { mode: "video" });
      setRequest(data);
      setOpen(true);
      setSecondsLeft(90);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start instant match");
    }
  };

  const cancel = async () => {
    if (request) {
      try {
        await api.post(`/instant-match/${request.request_id}/cancel`);
      } catch {}
    }
    setOpen(false);
    setRequest(null);
  };

  return (
    <>
      <Button
        onClick={start}
        size="lg"
        className="rounded-full bg-gradient-to-r from-[#C05640] to-[#E07A5F] hover:from-[#A64A37] hover:to-[#C26B53] text-white px-7 py-6 font-display text-base shadow-lg shadow-orange-500/20 hover:shadow-xl transition-all"
        data-testid="bored-now-button"
      >
        <Zap className="h-5 w-5 mr-2 fill-white" /> I&apos;m Bored Now
      </Button>

      <Dialog open={open} onOpenChange={(v) => !v && cancel()}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="instant-match-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Zap className="h-5 w-5 text-[#C05640] fill-[#C05640]" />
              Instant match
            </DialogTitle>
            <DialogDescription>
              15-minute micro-session · $12 flat
            </DialogDescription>
          </DialogHeader>

          {request?.status === "open" && (
            <div className="text-center py-6" data-testid="instant-waiting">
              <Loader2 className="h-10 w-10 text-[#C05640] mx-auto animate-spin" />
              <div className="font-display text-xl text-stone-900 mt-4">
                Looking for an available companion…
              </div>
              <div className="text-sm text-stone-500 mt-1">
                First to accept wins. Closing in <span className="font-semibold text-stone-700">{secondsLeft}s</span>.
              </div>
              <Button
                variant="outline"
                onClick={cancel}
                className="mt-5 rounded-full border-stone-300"
                data-testid="instant-cancel-button"
              >
                Cancel
              </Button>
            </div>
          )}

          {request?.status === "matched" && (
            <div className="text-center py-4" data-testid="instant-matched">
              <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
              {request.provider?.image && (
                <img
                  src={request.provider.image}
                  alt={request.provider.name}
                  className="h-16 w-16 rounded-full object-cover mx-auto mt-3 border-4 border-emerald-100"
                />
              )}
              <div className="font-display text-xl text-stone-900 mt-3">
                {request.provider?.name || "Your companion"} is ready!
              </div>
              <div className="text-sm text-stone-500 mt-1">
                {request.provider?.city || ""}
              </div>
              <Button
                onClick={() => navigate(`/bookings/${request.booking_id}`)}
                className="mt-5 w-full rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E]"
                data-testid="instant-go-to-booking"
              >
                Start the session →
              </Button>
            </div>
          )}

          {(request?.status === "expired" || request?.status === "cancelled") && (
            <div className="text-center py-6">
              <X className="h-10 w-10 text-stone-400 mx-auto" />
              <div className="font-display text-xl text-stone-900 mt-3">
                {request.status === "expired" ? "No one was free" : "Cancelled"}
              </div>
              <div className="text-sm text-stone-500 mt-1">
                {request.status === "expired"
                  ? "Try again, or browse companions to schedule one."
                  : "Your request was cancelled."}
              </div>
              <div className="flex gap-2 mt-5">
                <Button onClick={start} className="flex-1 rounded-full bg-[#4A5D4E] hover:bg-[#3B4A3E]">
                  Try again
                </Button>
                <Button variant="outline" onClick={() => setOpen(false)} className="flex-1 rounded-full">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
